import { useMemo } from 'react';
import { css } from '@emotion/react';
import { DataTableEmpty } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { SettingsSection } from '@frontend/settings-ui';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { SearchField, Button, useDebouncedValue, useFormField, Text, Chip, Tabs } from '@frontend/design-system';
import { useSyncSchedule } from '../../api/sync-bar/use-sync-schedule';
import { EmptyState } from '../../components/availability-rules/empty-state';
import { OperatoryContainer } from '../../components/availability-rules/operatory-container';
import { FormStateProvider } from '../../components/form-state-context';
import { settingsSectionSyncBarStyle, SyncBar } from '../../components/sync-bar';
import { UnpublishedModal } from '../../components/sync-bar/unpublished-modal';
import { AvailabilityDataProvider, useAvailabilityData } from '../../contexts/availability-data-context';
import { useChangesGuard } from '../../hooks/use-changes-guard';
import type { OperatoryWithAvailabilityRules } from '../../types/availability-rules.types';
import { RuleExceptionsPage } from './rule-exceptions-page';

type AvailabilityRulesPageProps = {
  locationId?: string;
};

const AvailabilityRulesContent = () => {
  const { t } = useTranslation('schedule-settings');
  const searchField = useFormField({ type: 'text' });
  const debouncedValue = useDebouncedValue(searchField.value, 500);
  const { selectedOrgId } = useAppScopeStore();

  const {
    data: combinedData,
    locationId,
    lastSyncedAt,
    refetchOperatoriesData,
    refetchAvailabilityRulesData,
    hasUnpublishedChanges,
    markAsPublished,
  } = useAvailabilityData();

  const { syncSchedule, isSyncing } = useSyncSchedule({
    orgId: selectedOrgId,
    locationId,
    onSuccess: () => {
      markAsPublished();
      unpublishedModalProps.onClose();
      proceed();
    },
  });

  const { modalProps: unpublishedModalProps, proceed } = useChangesGuard({
    getShouldBlock: () => hasUnpublishedChanges,
  });

  const refetch = () => {
    refetchOperatoriesData();
    refetchAvailabilityRulesData();
  };

  const exitUnpublishedModal = () => {
    markAsPublished();
    proceed();
  };

  const searchOperatoriesAndRules = useMemo(() => {
    const operatoriesWithRules = combinedData?.operatories || [];
    if (!debouncedValue) return operatoriesWithRules;
    const query = debouncedValue.toLowerCase();
    return operatoriesWithRules.reduce<OperatoryWithAvailabilityRules[]>((acc, operatory) => {
      if (operatory.name?.toLowerCase().includes(query)) {
        acc.push(operatory);
      } else {
        const matchingRules = operatory.availabilityRules.filter(
          (rule) =>
            rule.providers.some((provider) => provider.name.toLowerCase().includes(query)) ||
            rule.services.some((service) => service.name.toLowerCase().includes(query))
        );
        if (matchingRules.length > 0) {
          acc.push({ ...operatory, availabilityRules: matchingRules });
        }
      }
      return acc;
    }, []);
  }, [combinedData?.operatories, debouncedValue]);

  return (
    <>
      <SettingsSection css={calculatedAvailabilitySectionStyle}>
        <Text as='h3' css={{ fontWeight: theme.font.weight.bold, fontSize: theme.font.size.h3, width: 'fit-content' }}>
          {t('Schedule Availability')}
        </Text>
        <Chip variant='eggplant' leftElement={<Icon name='department-small' />} css={{ justifySelf: 'start' }}>
          {t('Operatory')}
        </Chip>
        <Text css={{ gridColumn: '1 / -1' }}>
          {t(
            'To calculate availability, both the assigned provider and the operatory must be available within the availability rule.'
          )}
        </Text>
      </SettingsSection>
      <SyncBar locationId={locationId} lastSyncedAt={lastSyncedAt ?? null} onPublishSuccess={markAsPublished} />
      <SettingsSection removeDefaultStyles css={settingsSectionSyncBarStyle}>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <div
            css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginLeft: 'auto', maxWidth: 340 }}
          >
            <SearchField
              {...searchField}
              name='operatory-search'
              placeholder={t('Search operatories')}
              css={{ flex: 1 }}
            />
            <Button
              variant='secondary'
              size='large'
              iconName='update'
              hoverLabel={t('Refetch')}
              onClick={() => refetch()}
            />
          </div>

          {searchOperatoriesAndRules.length === 0 ? (
            <EmptyState
              title={t('No operatories found')}
              description={t(
                "To add availability, operatories must be mapped and added to your organization's locations."
              )}
              image={<img src={DataTableEmpty} alt='Empty state' style={{ width: 200, height: 'auto' }} />}
            />
          ) : (
            <>
              {searchOperatoriesAndRules.map((operatory) => (
                <OperatoryContainer key={operatory.id} operatory={operatory} />
              ))}
            </>
          )}
        </div>
      </SettingsSection>
      <UnpublishedModal
        modalProps={unpublishedModalProps}
        onPublish={syncSchedule}
        onExit={exitUnpublishedModal}
        isPublishing={isSyncing}
      />
    </>
  );
};

export const AvailabilityRulesPage = ({ locationId }: AvailabilityRulesPageProps) => {
  const { t } = useTranslation('schedule-settings');
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const selectedLocationId = locationId || selectedLocationIds[0] || '';

  const [activeTab, setActiveTab] = usePreference('AIReceptionistScheduleSettingsActiveTab');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as 'availability-rules-tab' | 'rule-exceptions-tab');
  };

  return (
    <AvailabilityDataProvider locationId={selectedLocationId} orgId={selectedOrgId}>
      <FormStateProvider>
        <Tabs key={selectedLocationId} initialTab={activeTab ?? 'availability-rules-tab'} onChange={handleTabChange}>
          <Tabs.Bar>
            <Tabs.Tab id='availability-rules-tab'>{t('Availability Rules')}</Tabs.Tab>
            <Tabs.Tab id='rule-exceptions-tab'>{t('Rule Exceptions')}</Tabs.Tab>
          </Tabs.Bar>
          <Tabs.Panel controller='availability-rules-tab' css={{ marginTop: theme.spacing(2) }}>
            <AvailabilityRulesContent />
          </Tabs.Panel>
          <Tabs.Panel controller='rule-exceptions-tab' css={{ marginTop: theme.spacing(2) }}>
            <RuleExceptionsPage locationId={selectedLocationId} orgId={selectedOrgId} />
          </Tabs.Panel>
        </Tabs>
      </FormStateProvider>
    </AvailabilityDataProvider>
  );
};

const calculatedAvailabilitySectionStyle = css({
  marginBottom: theme.spacing(2),
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr 1fr',
  justifyContent: 'start',
  alignItems: 'center',
  gap: theme.spacing(1, 2),
  [`@media (max-width: ${breakpoints.xsmall.max}px)`]: { gridTemplateColumns: '1fr' },
});
